import { useTranslation } from 'next-i18next'

import { useAppSelector } from '~/redux/hooks'
import { selectPoints } from '~/gamification/slices/gamification.slice'
import useCurrentUser from '~/hooks/useCurrentUser'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import useCalculateLevel from '~/hooks/useCalculateLevel'
import { Breakpoint, useBreakpoint } from '~/utils/useBreakpoint'

import SVGImage from '../Image/SVGImage'

import getUserTitle from './UserLevel.utils'

export function UserLevel({
  className,
  xpBarColor,
  xpBarBG,
  dividingLineColor = 'border-white',
  children,
  textSize = { name: 'text-sm', title: 'text-xxs' },
  showXPOnly,
}: {
  className?: string
  xpBarColor?: string
  xpBarBG?: string
  dividingLineColor?: string
  children?: React.ReactElement
  textSize?: { name: string; title: string }
  showXPOnly?: boolean
}): React.ReactElement {
  const { t } = useTranslation('homebase')
  const user = useCurrentUser()
  const points = useAppSelector(selectPoints)
  const userLevelData = useCalculateLevel(points ? points : 0)
  const xpPercentToNextLvl = Math.floor((userLevelData.xpGainedThisLvl / userLevelData.xpToNextLvl) * 100)
  const userTitle = getUserTitle(userLevelData.lvl)

  const breakpoint = useBreakpoint()
  const isLessThenMDSize = breakpoint < Breakpoint.MD

  return (
    <div className={`flex space-x-2 pr-2 md:pt-2 ${className}`}>
      <div className="flex justify-center relative items-center w-10">
        <p className={`absolute block -mt-4 text-white z-10 ${textSize.name}`}>{userLevelData.lvl}</p>
        <ResponsiveImage
          alt={t('imageAlt.levelBadge')}
          height={54}
          imageClassName="w-10"
          srcKey="gamification/user-level-badge.png"
          width={40}
        />
      </div>
      <div className="flex flex-col">
        {!showXPOnly && (
          <>
            <h2 className={`font-semibold ${textSize.name}`}>{user?.nickname}</h2>
            {isLessThenMDSize && <span>{t(userTitle)}</span>}
          </>
        )}
        <div className={`${textSize.title} flex items-center ${children ? 'justify-between' : ''}`}>
          <div className="flex items-center">
            <span className="mr-1">{userLevelData.xpGainedThisLvl}</span>
            <span>/</span>
            <span className="mx-1">{userLevelData.xpToNextLvl}</span>
            <SVGImage
              alt="XP"
              height={showXPOnly ? 25 : 15}
              srcKey="gamification/xp.svg"
              width={showXPOnly ? 26.5 : 16}
            />
          </div>
          {children && isLessThenMDSize && <div className="flex item-center w-5">{children}</div>}
          {!isLessThenMDSize && !showXPOnly && (
            <>
              <span className={`border-r ${dividingLineColor} my-2 m-4 h-5/6`} />
              <span>{t(userTitle)}</span>
              <div className="flex item-center justify-center w-5 mx-1">{children}</div>
            </>
          )}
        </div>
        <div
          className={`${xpBarBG ? xpBarBG : 'bg-black'} bg-opacity-20 w-35 sm:w-50 h-1 rounded overflow-hidden mt-1`}
        >
          <div
            className={`${xpBarColor ? xpBarColor : 'bg-white'} h-full rounded`}
            style={{ width: `${xpPercentToNextLvl}%` }}
          />
        </div>
      </div>
    </div>
  )
}
